import { Badge } from '@/components/common/Badge'
import { InputTypeahead } from '@/components/common/Input'
import { Mana } from '@/components/common/Mana'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { usePage } from '@/components/providers/PageProvider'
import { RESET_DEFAULT, type TypeaheadOptionGroupType, type TypeaheadOptionType } from '@/defs/input'
import { COLOR_ID_GROUP_LABELS, COLOR_ID_LABELS, type ColorIdType } from '@/defs/mtg'
import { SCROLL_GALLERY } from '@/defs/scroll'
import { useLinkInternal } from '@/hooks/useLinkInternal'
import { isValidRoute } from '@/utils/routeUtil'

import styles from './FilterInputColorId.module.scss'
import { FilterInputColorIdChoices } from './FilterInputColorIdChoices/FilterInputColorIdChoices'

type Props = {
  showCheckboxes?: boolean
}

export const FilterInputColorId = ({ showCheckboxes }: Props) => {
  const isNavigating = useIsNavigating()
  const { page } = usePage()
  const { redirectRelative } = useLinkInternal()

  const route = page?.route
  const colorIdMap = page?.optionsFilters?.colorIdMap
  const colorIds = page?.optionsFilters?.colorIds || []
  const colorId = route?.colorId
  const canClearColorId = !!route && isValidRoute({ ...route, colorId: undefined })

  if (!colorIdMap) return null

  const optionGroups = COLOR_ID_GROUP_LABELS.map<TypeaheadOptionGroupType<ColorIdType>>((group) => ({
    Component: ({ children }) => (
      <div className={styles.group}>
        <p className={styles.groupLabel}>{group.label}</p>
        {children}
      </div>
    ),
    options: group.cis
      .filter((ci) => colorIds.includes(ci))
      .map<TypeaheadOptionType<ColorIdType>>((ci) => ({
        Component: ({ children }) => (
          <div className={styles.option}>
            <Mana colors={COLOR_ID_LABELS[ci].colors} />
            {children}
            {colorIdMap[ci] && <Badge count={colorIdMap[ci]} />}
          </div>
        ),
        label: COLOR_ID_LABELS[ci].label,
        value: ci,
      })),
  }))

  const onApply = (value?: ColorIdType) => redirectRelative({ colorId: value }, { scroll: SCROLL_GALLERY })

  const typeaheadElement = (
    <InputTypeahead
      aria-label='Search for a color identity'
      onClear={() => redirectRelative({ colorId: undefined }, { scroll: SCROLL_GALLERY })}
      onSelect={(value) => redirectRelative({ colorId: value || undefined }, { scroll: SCROLL_GALLERY })}
      optionGroups={optionGroups}
      optionReset={canClearColorId ? RESET_DEFAULT : undefined}
      placeholder='Choose a Color Identity...'
      showClear={canClearColorId}
      showLoadingMask={isNavigating}
      value={colorId || ''}
    />
  )

  if (!showCheckboxes) return typeaheadElement

  return (
    <div className={styles.container}>
      <FilterInputColorIdChoices
        colorId={colorId}
        colorIds={colorIds}
        key={colorId}
        loading={isNavigating}
        onApply={onApply}
        showEmpty={canClearColorId}
      />
      {typeaheadElement}
    </div>
  )
}
